/**
 * Intenção gastronómica — VERDADE ÚNICA E VINCULATIVA do produto.
 *
 * SEPARAÇÃO OBRIGATÓRIA DE SINAIS (dois conceitos distintos):
 *  - `foodStopIntent` controla restaurantes/cafés/comida como PARAGENS DE
 *    VISITA. Só nasce de ação explícita: texto que pede comida/gastronomia,
 *    chip Gastronomia tocado, categoria ou paragem pedida de comida, cozinha
 *    explícita, local obrigatório de comida, ou o botão "Incluir gastronomia"
 *    do cartão de escassez.
 *  - `mealMode` controla APENAS almoço/jantar (cartões de refeição).
 *    Nunca influencia `foodStopIntent` — nem o valor, nem o cálculo.
 *
 * Garantia vinculativa A — só ação explícita ativa comida:
 *   foodStopIntent = true ⟺ nesta sessão/nesse pedido houve uma ação ou
 *   pedido explícito da pessoa (ver acima). Nunca pode nascer do valor
 *   predefinido do formulário, de preferências guardadas, da rota anterior,
 *   de horários de refeição automáticos, do rádio de refeições nem de
 *   qualquer valor persistido.
 *
 * Regra de compatibilidade obrigatória: enquanto existir, `foodIntent` é
 * sempre um ALIAS EXATO de `foodStopIntent`. Nunca pode ser calculado a
 * partir de `mealMode`, de horários de almoço/jantar ou da sugestão
 * automática de restaurante.
 *
 * Garantia B — barreira final única: `isFoodPlace` decide apenas por
 * `primaryType`/`types` reais do Google Places. Nunca por nome, etiqueta,
 * categoria escrita ou texto.
 *
 * Módulo puro: sem IA, sem Maps, sem custos.
 */

import type { MealMode } from "./meal-preference";

/** Tipos oficiais do Google Places que são estabelecimentos alimentares. */
export const FOOD_PLACE_TYPES: ReadonlySet<string> = new Set([
  "restaurant",
  "cafe",
  "coffee_shop",
  "bakery",
  "bar",
  "pub",
  "brewery",
  "winery",
  "distillery",
  "food_court",
  "food",
  "meal_takeaway",
  "meal_delivery",
  "ice_cream_shop",
  "dessert_shop",
  "confectionery",
]);

export type FoodPlaceEvidence = {
  primaryType?: string | null;
  types?: string[] | null;
};

/**
 * Barreira final: um lugar é alimentar se (e só se) os tipos reais do Places
 * o dizem. Um prefixo "meal_" cobre tipos futuros da mesma família.
 */
export function isFoodPlace(place: FoodPlaceEvidence | null | undefined): boolean {
  if (!place) return false;
  const primary = (place.primaryType ?? "").toLowerCase();
  if (primary && (FOOD_PLACE_TYPES.has(primary) || primary.startsWith("meal_"))) return true;
  for (const type of place.types ?? []) {
    const value = (type ?? "").toLowerCase();
    if (!value) continue;
    if (FOOD_PLACE_TYPES.has(value) || value.startsWith("meal_")) return true;
  }
  return false;
}

export type ComputeFoodIntentInput = {
  /** Texto do pedido ATUAL (nunca o anterior). */
  requestText?: string | null;
  /** Chips explicitamente tocados neste pedido. */
  explicitInterests?: string[] | null;
  /** Interesses detetados no texto deste pedido. */
  textInterests?: string[] | null;
  /**
   * Sinal SEPARADO de almoço/jantar (cartões de refeição). É apenas
   * transportado para o log/decisão — NUNCA influencia foodStopIntent.
   */
  mealMode?: MealMode | null;
  categoryFocus?: string | null;
  requestedStopCategory?: string | null;
  requestedStopQuery?: string | null;
  categoryFocusQuery?: string | null;
  cuisine?: string | null;
  /** Locais obrigatórios nomeados ("Torre de Belém", "Cervejaria X"). */
  requiredPlaces?: string[] | null;
  /** Queries de restrições obrigatórias ("um restaurante japonês"). */
  requiredStopQueries?: string[] | null;
  /** Notas livres do pedido atual. */
  notes?: string | null;
  /** Botão "Incluir gastronomia" do cartão de escassez — ação explícita. */
  includeFood?: boolean;
};

const FOOD_INTEREST_IDS = new Set(["food", "gastronomy", "gastronomia", "foodie", "comida"]);

const FOOD_CATEGORY_FOCUS = new Set([
  "restaurant",
  "cafe",
  "sweets",
  "breakfast",
]);

/** Frases que pedem uma EXPERIÊNCIA gastronómica (tour, roteiro de comida…). */
const FOOD_EXPERIENCE_PATTERNS: RegExp[] = [
  /\b(?:roteiro|tour|percurso|experience|experi[eê]ncia)\s+(?:gastron[oó]mic\w+|de\s+(?:restaurantes|comida|tascas|petiscos)|food)\b/i,
  /\bfood\s+(?:tour|crawl)\b/i,
  /\b(?:visitar|visit)\s+(?:restaurantes|caf[eé]s|bares|tascas|pastelarias)\b/i,
  /\b(?:quero|queremos|gostava|gostaria|i\s+want|we\s+want|quiero|voglio|je\s+veux)[^.;!?\n]{0,40}\b(?:gastronomi\w+|comida|comer|eat|food)\b/i,
  /\b(?:onde\s+comer|where\s+to\s+eat|d[oó]nde\s+comer|dove\s+mangiare|wo\s+(?:kann\s+man\s+)?essen)\b/i,
];

/** Negar comida é vinculativo: anula qualquer outra fonte. */
const FOOD_NEGATION_PATTERN =
  /\b(?:sem|não\s+quero|nao\s+quero|no|without|sin|sans|senza|keine)\b[^.;!?\n]{0,40}\b(?:restaurantes?|refei\w+|comida|gastronomia|caf[eé]s|bares|food|restaurants?|meals?)\b/i;

export type FoodIntentDecision = {
  /** Restaurantes/cafés como paragens de visita. Só texto/chips/ações. */
  foodStopIntent: boolean;
  /**
   * Regra de compatibilidade obrigatória: alias EXATO de foodStopIntent.
   * Nunca derivado de mealMode, horários de refeição ou sugestão automática.
   */
  foodIntent: boolean;
  /** Sinal independente de almoço/jantar — nunca afeta foodStopIntent. */
  mealMode: MealMode;
  /** Modo de visita genérico: ver a cidade, sem foco nem intenção alimentar. */
  genericVisitMode: boolean;
  /** Fontes que ativarão (ou não) a intenção — para log estruturado. */
  reasons: string[];
};

/**
 * Decisão vinculativa de intenção gastronómica + modo de visita genérico.
 * Determinística: o mesmo input dá sempre a mesma decisão.
 */
export function computeFoodIntent(input: ComputeFoodIntentInput): FoodIntentDecision {
  const text = [input.requestText ?? "", input.notes ?? ""].join(" ").trim();
  const reasons: string[] = [];

  const explicitInterests = (input.explicitInterests ?? []).map((v) => v.toLowerCase());
  const textInterests = (input.textInterests ?? []).map((v) => v.toLowerCase());
  const categoryFocus = input.categoryFocus ?? null;
  const requestedStopCategory = input.requestedStopCategory ?? null;
  const requestedStopQuery = input.requestedStopQuery ?? null;
  const categoryFocusQuery = input.categoryFocusQuery ?? null;
  const cuisine = input.cuisine ?? null;
  // mealMode é apenas transportado — nunca entra no cálculo abaixo.
  const mealMode = input.mealMode ?? "restaurant_suggestions";

  // 1) Ações explícitas da pessoa nesta sessão. NOTA: o rádio de refeições
  // ("restaurant") NÃO ativa comida como paragem — pertence ao mealMode.
  if (input.includeFood) reasons.push("include_food_button");
  if (explicitInterests.some((i) => FOOD_INTEREST_IDS.has(i))) reasons.push("chip_food");
  if (categoryFocus && FOOD_CATEGORY_FOCUS.has(categoryFocus)) reasons.push(`category_focus:${categoryFocus}`);
  if (requestedStopCategory && FOOD_CATEGORY_FOCUS.has(requestedStopCategory)) reasons.push(`requested_category:${requestedStopCategory}`);
  if (cuisine) reasons.push(`cuisine:${cuisine}`);

  // 2) Texto do pedido atual: paragens/categorias pedidas de comida.
  const FOOD_CATEGORY_WORD =
    /\b(?:restaurante?s?|caf[eé]s?|pastelari\w+|padari\w+|bares|tascas?|gastronomia|comida|where\s+to\s+eat|onde\s+comer|food|restaurants?|caf[eé]s?|ristorant\w+|restaurantes?)\b/i;
  if (requestedStopQuery && FOOD_CATEGORY_WORD.test(requestedStopQuery)) reasons.push("requested_stop_food");
  if (categoryFocusQuery && FOOD_CATEGORY_WORD.test(categoryFocusQuery)) reasons.push("category_focus_query_food");
  for (const query of input.requiredStopQueries ?? []) {
    if (query && FOOD_CATEGORY_WORD.test(query)) reasons.push("required_constraint_food");
  }
  for (const place of input.requiredPlaces ?? []) {
    if (place && FOOD_CATEGORY_WORD.test(place)) reasons.push("required_place_food");
  }

  // 3) Interesses detetados no texto atual (nunca herdados).
  if (textInterests.some((i) => FOOD_INTEREST_IDS.has(i))) reasons.push("text_food_interest");

  // 4) Negação escrita: vence sempre tudo o resto.
  const negated = FOOD_NEGATION_PATTERN.test(text);
  if (negated) reasons.push("written_negation");

  const foodStopIntent = !negated && reasons.length > 0;
  // Alias exato — regra de compatibilidade obrigatória.
  const foodIntent = foodStopIntent;

  // Modo genérico: nenhum local específico, nenhuma categoria/interesse
  // escrito, nenhum chip, nenhum pedido de gastronomia.
  const genericVisitMode =
    !foodStopIntent &&
    !categoryFocus &&
    !requestedStopCategory &&
    !requestedStopQuery &&
    !categoryFocusQuery &&
    !cuisine &&
    (input.requiredPlaces ?? []).length === 0 &&
    (input.requiredStopQueries ?? []).length === 0 &&
    explicitInterests.length === 0 &&
    textInterests.length === 0;

  return { foodStopIntent, foodIntent, mealMode, genericVisitMode, reasons };
}
